import { Component, ElementRef, HostListener, Injector, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { BehaviorSubject, combineLatest, of, Subscription, timer } from 'rxjs';
import { catchError, debounce, distinctUntilChanged, filter, map } from 'rxjs/operators';
import moment from 'moment-timezone';
import * as am4core from '@amcharts/amcharts4/core';

import { AbstractChartOptionsModel } from '../../../../../Common/Charts/models/abstract-chart-options.model';
import { BucketFunctionApiEnum } from '../../../../../api/mentions/enum/bucket-function-api.enum';
import { CardChartComponentHighlightColorEnum, CardChartComponentLayoutEnum } from '../../../../../Common/Charts/components/CardChart/card-chart.component';
import { ChartGroupInputModel } from '../../../../../Common/Charts/models/chart-group-input.model';
import { ChartMetadataModel } from '../../../../../Common/Charts/models/chart-metadata.model';
import { DataPointModel } from '../../../../../Common/Charts/models/data-point.model';
import { EngagementAgeApiEnum } from '../../../../../api/mentions/enum/engagement-age-api.enum';
import { NeoFacetTermFieldApiEnum } from '../../../../../api/mentions/enum/neo-facet-term-field-api.enum';
import { ChartModel } from '../../../../../Common/Charts/models/chart.model';
import { LabelDisplayOptionsEnum } from '../../../../../Common/Misc/enums/label-display-options.enum';
import { MultiSeriesPieChartModel } from '../../../../../Common/Charts/models/pieChart/multiSeriesPieChart/multi-series-pie-chart.model';

import { UserPreferencesService } from '../../../../../Account/UserPreferences/services/user-preferences.service';
import { MediaTypeEnum } from '../../../../../Common/Basic/enums/media-type.enum';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { MultiSeriesDateAndValueXYChart } from '../../../../../Common/Charts/models/multi-series-date-and-value-x-y.chart';
import { NetworkTypeEnum } from '../../../../Core/enums/network-type.enum';
import { SocialChartDetailsDialogService } from '../../../services/social-chart-details-dialog.service';
import { ReportSeriesFacetGeneratorEnum } from '../../../../../Common/Reports/models/report-series-facet-generator.enum';
import { SocialSeriesNeoFacetKeysEnum } from '../../../enums/social-series-neo-facet-keys.enum';
import { SocialSeriesService } from '../../../services/social-series.service';
import { ThemeAnalyticsChartOptionsService } from '../theme-analytics-chart-options.service';
import { ThemeAnalyticsChartSeriesService } from '../theme-analytics-chart-series.service';
import { ThemeAnalyticsChartTypeEnum } from '../../../enums/theme-analytics-chart-type.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { SortOrderEnum } from '../../../../../api/shared/enums/sort-order.enum';
import { Mention } from '../../../../../Common/Mentions/models/mention';
import { MentionSearchModel } from '../../../../../Monitor/Shared/models/mention-search.model';
import { NeoFacetDateFieldApiEnum } from '../../../../../api/mentions/model/neo-facet-date-field-api.enum';
import { NeoFacetRequestApiModel } from '../../../../../api/mentions/model/neo-facet-request-api.model';
import { networkTypeToMediaTypeModelMap } from '../../../models/network-type-to-media-type-model.map';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { MentionSearchFacetTypesEnum } from '../../../../../api/mentions/enum/mention-search-facet-types.enum';
import { MediaSubscription } from '../../../../../Monitor/Shared/models/media-subscription.model';
import { ThemeService } from '../../../services/theme.service';
import { DropdownTypesEnum } from '../../../../../Common/Forms/enums/dropdown-types.enum';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { MentionSearchCriteriaModel } from '../../../../../Monitor/Shared/models/mention-search-criteria.model';
import { MediaTypeModel } from '../../../../../Monitor/Shared/models/media-type.model';
import { SentimentModel } from '../../../../../Monitor/Shared/models/sentiment.model';
import { DateQueryModel } from '../../../../../Common/Forms/models/date-query.model';
import { AgentService } from '../../../services/agent.service';
import { SocialSeriesMetricsEnum } from '../../../enums/social-series-metrics.enum';
import { ChartLoadingOptionsEnum } from '../../../../../Common/Charts/enums/chart-loading-options.enum';
import { SocialRoutingService } from '../../../../Core/services/social-routing.service';
import { SocialArchiveSearchService } from '../../../services/social-archive-search.service';
import { SocialChartExportModel, SocialChartExportService } from '../../../services/social-chart-export.service';
import { ChartExportEventModel } from '../../../../../Common/Charts/models/chart-export-event.model';
import { CustomLegendContainerItem, CustomLegendItem } from '../../../../../Common/Charts/models/custom-legend-item.model';
import { AgprComponentStyleEnum } from '../../../../../Common/Basic/enums/social-analysis-types.enum';
import { userAppPreferencesDefault } from '../../../../../Core/Users/data/user-app-preferences-default';
import { defaultSocialColorMap } from '../../../../../Core/Users/data/default-social-color-map';
import { ReportSeriesModel } from '../../../../../Common/Reports/models/report-series.model';
import { getTimeScale } from '../../../../../Common/Forms/helpers/time.helper';

@Component({
    selector: 'agpr-theme-analytics-summary',
    templateUrl: './theme-analytics-summary.component.html',
    styleUrls: ['./theme-analytics-summary.component.scss'],
    standalone: false
})
export class ThemeAnalyticsSummaryComponent implements OnInit, OnDestroy {
    readonly dropdownTypes = DropdownTypesEnum;
    readonly dateBulletFormat: string = 'YYYY/MM/DD hh:mm';
    readonly socialMetrics = SocialSeriesMetricsEnum;
    readonly chartLoadingOptionsEnum = ChartLoadingOptionsEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;
    readonly defaultChartColors = userAppPreferencesDefault.reportsPrefs.defaultChartColors;

    @Input() id: string;

    noContentLabelText: string;
    noContentLabel: string;

    // Card charts at the top of the page
    public totalVolumeSeries: ReportSeriesModel;
    public previousPeriodTotalVolumeSeries: ReportSeriesModel;

    public totalImpressionsSeries: ReportSeriesModel;
    public previousPeriodTotalImpressionsSeries: ReportSeriesModel;

    public totalEngagementsSeries: ReportSeriesModel;
    public previousPeriodTotalEngagementsSeries: ReportSeriesModel;

    // Share of voice by social network triple double pie chart
    public shareOfVoiceBySocialNetwork: ChartGroupInputModel;
    public shareOfVoiceBySocialNetworkSubCharts: MultiSeriesPieChartModel[];
    public shareOfVoiceBySocialNetworkLoading: boolean;
    private shareOfVoiceBySocialNetworkSubscription: Subscription;

    private shareOfVoiceBySocialNetworkVolumeSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialNetworkVolumeSeries: ReportSeriesModel;

    private shareOfVoiceBySocialNetworkImpressionsSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries: ReportSeriesModel;

    private shareOfVoiceBySocialNetworkEngagementsSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries: ReportSeriesModel;

    private shareOfVoiceBySocialNetworkChartOptions: AbstractChartOptionsModel = {};
    private shareOfVoiceBySocialNetworkChartMetadata: ChartMetadataModel;

    // Summary by Social Network - data over time + card charts
    private summaryBySocialNetworkVolumeOverTime: ReportSeriesModel;
    private summaryBySocialNetworkImpressionsOverTime: ReportSeriesModel;
    private summaryBySocialNetworkEngagementOverTime: ReportSeriesModel;

    public summaryBySocialNetworkChart: MultiSeriesDateAndValueXYChart;

    public summaryBySocialNetworkVolumeSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialNetworkVolumeSeries: ReportSeriesModel;

    public summaryBySocialNetworkImpressionsSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialNetworkImpressionsSeries: ReportSeriesModel;

    public summaryBySocialNetworkEngagementsSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialNetworkEngagementsSeries: ReportSeriesModel;

    // Share of voice by social topic triple double pie chart
    public shareOfVoiceBySocialTopic: ChartGroupInputModel;
    public shareOfVoiceBySocialTopicSubCharts: MultiSeriesPieChartModel[];

    private shareOfVoiceBySocialTopicVolumeSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialTopicVolumeSeries: ReportSeriesModel;

    private shareOfVoiceBySocialTopicImpressionsSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialTopicImpressionsSeries: ReportSeriesModel;

    private shareOfVoiceBySocialTopicEngagementsSeries: ReportSeriesModel;
    private previousPeriodShareOfVoiceBySocialTopicEngagementsSeries: ReportSeriesModel;

    public shareOfVoiceBySocialTopicChartOptions: AbstractChartOptionsModel = {};
    public shareOfVoiceBySocialTopicChartMetadata: ChartMetadataModel;

    // Summary by social topic - data over time + card charts
    private summaryBySocialTopicVolumeOverTime: ReportSeriesModel;
    private summaryBySocialTopicImpressionsOverTime: ReportSeriesModel;
    private summaryBySocialTopicEngagementOverTime: ReportSeriesModel;

    public summaryBySocialTopicChart: MultiSeriesDateAndValueXYChart;

    public summaryBySocialTopicVolumeSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialTopicVolumeSeries: ReportSeriesModel;

    public summaryBySocialTopicImpressionsSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialTopicImpressionsSeries: ReportSeriesModel;

    public summaryBySocialTopicEngagementsSeries: ReportSeriesModel;
    public previousPeriodSummaryBySocialTopicEngagementsSeries: ReportSeriesModel;

    public summaryBySocialTopicDropdownOptions: DropdownOptionModel[];
    public summaryBySocialTopicSelection: DropdownOptionModel;

    // Sentiment summary
    public sentimentSummaryChart: MultiSeriesPieChartModel;
    private sentimentSummarySeries: ReportSeriesModel;
    private previousPeriodSentimentSummarySeries: ReportSeriesModel;

    // Top Posts
    public topPostsSeries: ReportSeriesModel;
    public topPostsSortField: SearchCriteriaSortFieldEnum;

    idToChartModelMap: Map<string, ChartModel | ChartGroupInputModel>;

    public readonly socialTypes: NetworkTypeEnum[] = Object.values(NetworkTypeEnum);
    public readonly networkTypeEnum = NetworkTypeEnum;
    public enabledNetworkTypes: NetworkTypeEnum[] = [
        NetworkTypeEnum.all,
        NetworkTypeEnum.twitter,
        NetworkTypeEnum.facebook,
        NetworkTypeEnum.instagram,
        NetworkTypeEnum.youtube,
        NetworkTypeEnum.reddit,
        NetworkTypeEnum.linkedin
    ];
    public currentShareOfVoiceSocialType: NetworkTypeEnum = NetworkTypeEnum.twitter;

    readonly cardChartComponentHighlightColorEnum = CardChartComponentHighlightColorEnum;
    readonly cardChartComponentLayoutEnum = CardChartComponentLayoutEnum;
    readonly supportedExportFormats = [ 'png', 'jpg', 'svg', 'json', 'csv', 'print' ];
    readonly themeAnalyticsChartTypeEnum = ThemeAnalyticsChartTypeEnum;
    private socialTopics: MediaSubscription[];
    private subscriptions: Subscription[] = [];
    private agentIds: number[] = [];
    isArchiveSearch: boolean = false;
    enableEngagementInsights: boolean = false;
    hideSeries: ReportSeriesModel[] = [];
    disabledSeries: ReportSeriesModel[] = [];
    chartColors: string[] = [];

    @ViewChild('sentimentSummaryChartLegend', {read: ElementRef, static: true}) sentimentSummaryChartLegend: ElementRef;

    constructor(
        public localizationService: LocalizationService,
        public themeAnalyticsChartSeriesService: ThemeAnalyticsChartSeriesService,
        public themeAnalyticsChartOptionsService: ThemeAnalyticsChartOptionsService,
        public socialSeriesService: SocialSeriesService,
        public socialChartDetailsDialogService: SocialChartDetailsDialogService,
        public coreDialogService: CoreDialogService,
        public injector: Injector,
        public agentService: AgentService,
        protected themeService: ThemeService,
        private socialRoutingService: SocialRoutingService,
        private socialArchiveSearchService: SocialArchiveSearchService,
        public chartExportService: SocialChartExportService,
        public userPreferencesService: UserPreferencesService
    ) {
        this.noContentLabelText = this.localizationService.transform('Social.intelligence.wordCloudChart.noContent');
        this.noContentLabel = `<span style="font-size: 0.875rem;">${this.noContentLabelText}</span>`;
    }

    @HostListener('click', ['$event.target'])
    onClick(event){
        if (event?.classList.contains('viewMentionLink') && event?.classList.length > 1) {
            const chartClass = event.classList[1];
            const chartModel = this.idToChartModelMap.get(chartClass);

            this.socialChartDetailsDialogService.openDrillDownPage(
                event.href,
                chartModel.clone ? chartModel.clone() : chartModel
            );
            return false;
        }
    }

    ngOnInit(): void {
        this.subscriptions.push(
            combineLatest(
                [
                    this.agentService.all$.pipe(filter(x => !!x)),
                    this.socialSeriesService.agentIds$.pipe(filter(x => !!x)),
                    this.socialRoutingService.isArchiveSearch$,
                    this.socialSeriesService.availableNetworks$.pipe(
                        filter(x => !!x),
                        distinctUntilChanged((prev, curr) => {
                            // emit only when the network list actually changes
                            return prev.join(',') === curr.join(',');
                        }
                        )),
                    this.socialArchiveSearchService.enableEngagementInsights$
                ]
            ).pipe(debounce(x => timer(500))).subscribe(
                ([socialTopics, agentIds, isArchiveSearch, availableNetworks, enableEngagementInsights]) => {
                    this.enabledNetworkTypes = availableNetworks;
                    this.socialTopics = socialTopics;
                    this.agentIds = agentIds;
                    this.isArchiveSearch = isArchiveSearch;
                    this.enableEngagementInsights = enableEngagementInsights;
                    this.deregisterSeries();

                    this.summaryBySocialTopicDropdownOptions = this.agentIds.map(
                        id => <DropdownOptionModel>{
                            label: this.socialTopics.find(socialTopic => socialTopic.id === +id).name,
                            value: id
                        }
                    );
                    this.summaryBySocialTopicSelection = this.summaryBySocialTopicDropdownOptions[0];

                    this.initializeAll();
                }
            ),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors.generalColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors))
            ).subscribe(generalColors => {
                this.chartColors = Object.values(generalColors);
            }),
        );
    }

    ngOnDestroy(): void {
        this.deregisterSeries();
        this.subscriptions.forEach(sub => sub.unsubscribe());
        if (this.shareOfVoiceBySocialNetworkSubscription) {
            this.shareOfVoiceBySocialNetworkSubscription.unsubscribe();
        }
    }

    deregisterSeries() {
        this.socialSeriesService.deregisterSeries([
            // Top cards
            this.totalVolumeSeries,
            this.previousPeriodTotalVolumeSeries,
            this.totalImpressionsSeries,
            this.previousPeriodTotalImpressionsSeries,
            this.totalEngagementsSeries,
            this.previousPeriodTotalEngagementsSeries,
            // Share of voice by social network
            this.shareOfVoiceBySocialNetworkVolumeSeries,
            this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries,
            this.shareOfVoiceBySocialNetworkImpressionsSeries,
            this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries,
            this.shareOfVoiceBySocialNetworkEngagementsSeries,
            this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries,
            // Summary by social network - data over time + 3 cards
            this.summaryBySocialNetworkVolumeOverTime,
            this.summaryBySocialNetworkImpressionsOverTime,
            this.summaryBySocialNetworkEngagementOverTime,
            this.summaryBySocialNetworkVolumeSeries,
            this.previousPeriodSummaryBySocialNetworkVolumeSeries,
            this.summaryBySocialNetworkImpressionsSeries,
            this.previousPeriodSummaryBySocialNetworkImpressionsSeries,
            this.summaryBySocialNetworkEngagementsSeries,
            this.previousPeriodSummaryBySocialNetworkEngagementsSeries,
            // Share of voice by social topic
            this.shareOfVoiceBySocialTopicVolumeSeries,
            this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries,
            this.shareOfVoiceBySocialTopicImpressionsSeries,
            this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries,
            this.shareOfVoiceBySocialTopicEngagementsSeries,
            this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries,
            // Summary by social Topic - data over time + 3 cards
            this.summaryBySocialTopicVolumeOverTime,
            this.summaryBySocialTopicImpressionsOverTime,
            this.summaryBySocialTopicEngagementOverTime,
            this.summaryBySocialTopicVolumeSeries,
            this.previousPeriodSummaryBySocialTopicVolumeSeries,
            this.summaryBySocialTopicImpressionsSeries,
            this.previousPeriodSummaryBySocialTopicImpressionsSeries,
            this.summaryBySocialTopicEngagementsSeries,
            this.previousPeriodSummaryBySocialTopicEngagementsSeries,
            // Sentiment Summary
            this.sentimentSummarySeries,
            this.previousPeriodSentimentSummarySeries,
            // Top posts
            this.topPostsSeries
        ].filter(x => ![...this.hideSeries, ...this.disabledSeries]?.find(series => x === series)));
    }

    initializeAll(): void {
        this.initializeCards();
        this.initializeShareOfVoiceBySocialNetwork();
        this.initializeSummaryBySocialNetworkChart();
        this.initializeShareOfVoiceBySocialTopic();
        this.initializeSummaryBySocialTopicChart();
        this.initializeSentimentSummaryChart();
        this.initializeTopPosts();

        this.idToChartModelMap = new Map<string, ChartModel | ChartGroupInputModel>([
            // the string here should match the id in the tooltip link id in tooltipHTML
            ['shareOfVoiceBySocialNetwork', this.shareOfVoiceBySocialNetwork],
            ['shareOfVoiceBySocialTopic', this.shareOfVoiceBySocialTopic],
            ['sentimentSummaryChart', this.sentimentSummaryChart],
            ['summaryBySocialNetwork', this.summaryBySocialNetworkChart],
            ['summaryBySocialTopic', this.summaryBySocialTopicChart]
        ]);

        if (this.isArchiveSearch) {
            this.hideSeries = [
                // Top cards
                this.previousPeriodTotalVolumeSeries,
                this.previousPeriodTotalImpressionsSeries,
                this.previousPeriodTotalEngagementsSeries,
                // Share of voice by social network
                this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries,
                this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries,
                this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries,
                // Summary by social network - data over time + 3 cards
                this.previousPeriodSummaryBySocialNetworkVolumeSeries,
                this.previousPeriodSummaryBySocialNetworkImpressionsSeries,
                this.previousPeriodSummaryBySocialNetworkEngagementsSeries,
                // Share of voice by social topic
                this.shareOfVoiceBySocialTopicVolumeSeries,
                this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries,
                this.shareOfVoiceBySocialTopicImpressionsSeries,
                this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries,
                this.shareOfVoiceBySocialTopicEngagementsSeries,
                this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries,
                // Summary by social Topic - data over time + 3 cards
                this.summaryBySocialTopicVolumeOverTime,
                this.summaryBySocialTopicImpressionsOverTime,
                this.summaryBySocialTopicEngagementOverTime,
                this.summaryBySocialTopicVolumeSeries,
                this.previousPeriodSummaryBySocialTopicVolumeSeries,
                this.summaryBySocialTopicImpressionsSeries,
                this.previousPeriodSummaryBySocialTopicImpressionsSeries,
                this.summaryBySocialTopicEngagementsSeries,
                this.previousPeriodSummaryBySocialTopicEngagementsSeries,
                // Sentiment Summary
                this.previousPeriodSentimentSummarySeries,
            ];

            if (!this.enableEngagementInsights) {
                this.disabledSeries = [
                    // Top cards
                    this.totalEngagementsSeries,
                    // Share of voice by social network
                    this.shareOfVoiceBySocialNetworkEngagementsSeries,
                    // Summary by social network - data over time + 3 cards
                    this.summaryBySocialNetworkEngagementOverTime,
                    this.summaryBySocialNetworkEngagementsSeries,
                ];
            } else {
                this.disabledSeries = [];
            }
        } else {
            this.hideSeries = [];
        }

        this.socialSeriesService.fulfillSeries(
            [
                // Top cards
                this.totalVolumeSeries,
                this.previousPeriodTotalVolumeSeries,
                this.totalImpressionsSeries,
                this.previousPeriodTotalImpressionsSeries,
                this.totalEngagementsSeries,
                this.previousPeriodTotalEngagementsSeries,
                // Share of voice by social network
                this.shareOfVoiceBySocialNetworkVolumeSeries,
                this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries,
                this.shareOfVoiceBySocialNetworkImpressionsSeries,
                this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries,
                this.shareOfVoiceBySocialNetworkEngagementsSeries,
                this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries,
                // Summary by social network - data over time + 3 cards
                this.summaryBySocialNetworkVolumeOverTime,
                this.summaryBySocialNetworkImpressionsOverTime,
                this.summaryBySocialNetworkEngagementOverTime,
                this.summaryBySocialNetworkVolumeSeries,
                this.previousPeriodSummaryBySocialNetworkVolumeSeries,
                this.summaryBySocialNetworkImpressionsSeries,
                this.previousPeriodSummaryBySocialNetworkImpressionsSeries,
                this.summaryBySocialNetworkEngagementsSeries,
                this.previousPeriodSummaryBySocialNetworkEngagementsSeries,
                // Share of voice by social topic
                this.shareOfVoiceBySocialTopicVolumeSeries,
                this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries,
                this.shareOfVoiceBySocialTopicImpressionsSeries,
                this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries,
                this.shareOfVoiceBySocialTopicEngagementsSeries,
                this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries,
                // Summary by social Topic - data over time + 3 cards
                this.summaryBySocialTopicVolumeOverTime,
                this.summaryBySocialTopicImpressionsOverTime,
                this.summaryBySocialTopicEngagementOverTime,
                this.summaryBySocialTopicVolumeSeries,
                this.previousPeriodSummaryBySocialTopicVolumeSeries,
                this.summaryBySocialTopicImpressionsSeries,
                this.previousPeriodSummaryBySocialTopicImpressionsSeries,
                this.summaryBySocialTopicEngagementsSeries,
                this.previousPeriodSummaryBySocialTopicEngagementsSeries,
                // Sentiment Summary
                this.sentimentSummarySeries,
                this.previousPeriodSentimentSummarySeries,
                // Top posts
                this.topPostsSeries
            ].filter(x => !([...this.hideSeries, ...this.disabledSeries].find(series => x === series)))
        );
        [...this.hideSeries, ...this.disabledSeries].forEach(series => series.dataProvider.next([]));
    }

    public initializeCards(): void {
        // total volume series setup
        this.totalVolumeSeries = new ReportSeriesModel();
        this.totalVolumeSeries.label = 'Social.intelligence.themeAnalytics.chart.totalVolumeLabel';
        this.totalVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };

        // previous period total volume series setup
        this.previousPeriodTotalVolumeSeries = new ReportSeriesModel();
        this.previousPeriodTotalVolumeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };

        // total impressions series setup
        this.totalImpressionsSeries = new ReportSeriesModel();
        this.totalImpressionsSeries.label = 'Social.intelligence.themeAnalytics.chart.totalImpressionsLabel';
        this.totalImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            );

            return [{
                count: count
            }];
        };

        // previous period total volume series setup
        this.previousPeriodTotalImpressionsSeries = new ReportSeriesModel();
        this.previousPeriodTotalImpressionsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            );

            return [{
                count: count
            }];
        };

        // total engagement series setup
        this.totalEngagementsSeries = new ReportSeriesModel();
        this.totalEngagementsSeries.label = 'Social.intelligence.themeAnalytics.chart.totalEngagementsLabel';
        this.totalEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.totalEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            );
            return [{
                count: count
            }];
        };

        // previous period total volume series setup
        this.previousPeriodTotalEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodTotalEngagementsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodTotalEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodTotalEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            );

            return [{
                count: count
            }];
        };
    }

    public initializeShareOfVoiceBySocialNetwork() {
        const subChartTitles = [
            this.localizationService.transform('Social.intelligence.themeAnalytics.volume'),
            this.localizationService.transform('Social.intelligence.themeAnalytics.impressions'),
            this.localizationService.transform('Social.intelligence.themeAnalytics.engagements'),
        ];

        this.shareOfVoiceBySocialNetworkSubCharts = [];

        // volume series setup
        this.shareOfVoiceBySocialNetworkVolumeSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialNetworkVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialNetworkVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.count,
                    viewMentionUrl: this.shareOfVoiceBySocialNetworkVolumeSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };

        // previous volume series setup
        this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.count,
                    viewMentionUrl: this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };

        // impressions series setup
        this.shareOfVoiceBySocialNetworkImpressionsSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialNetworkImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialNetworkImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count,
                    viewMentionUrl: this.shareOfVoiceBySocialNetworkImpressionsSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };

        // previous impressions setup
        this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count,
                    viewMentionUrl: this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };
        this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.mentions.isPreviousPeriod = true;

        // engagement series setup
        this.shareOfVoiceBySocialNetworkEngagementsSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialNetworkEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialNetworkEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                    viewMentionUrl: this.shareOfVoiceBySocialNetworkEngagementsSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };

        // previous engagement series setup
        this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(bucket => {
                const filterOverrides = new MentionSearchCriteriaModel();

                // map 'TWEET' for backwards compatibility
                if (bucket.termValue === 'TWEET') {
                    bucket.termValue = MediaTypeEnum.TWITTER;
                }

                filterOverrides.query.includedMediaTypes = [ new MediaTypeModel(bucket.termValue as MediaTypeEnum)];
                filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                return <DataPointModel>{
                    id: bucket.termValue,
                    label: this.localizationService.transform(`MediaType.${bucket.termValue as MediaTypeEnum}`),
                    xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                    viewMentionUrl: this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.getSocialSeriesUrl(filterOverrides)
                };
            });
        };
        this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.mentions.isPreviousPeriod = true;

        // creating the chart model by joining series, options and metadata for all three double pie charts
        for (let i = 0; i < 3; i++) {
            let series;

            if (this.isArchiveSearch) {
                series = [
                    [this.shareOfVoiceBySocialNetworkVolumeSeries],
                    [this.shareOfVoiceBySocialNetworkImpressionsSeries],
                    [this.shareOfVoiceBySocialNetworkEngagementsSeries]
                ][i];
            } else {
                series = [
                    [this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries, this.shareOfVoiceBySocialNetworkVolumeSeries],
                    [this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries, this.shareOfVoiceBySocialNetworkImpressionsSeries],
                    [this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries, this.shareOfVoiceBySocialNetworkEngagementsSeries]
                ][i];
            }

            let disableEngagement: boolean = false;
            // the third chart is shareOfVoiceBySocialNetworkEngagementsSeries
            // we want to show custom titleHtml and tooltip if enableEngagementInsights is false
            if (!this.enableEngagementInsights && this.isArchiveSearch && i === 2) {
                disableEngagement = true;
            }

            const options = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK_SUB);
            const metadata = {
                titleText: subChartTitles[i],
                // eslint-disable-next-line max-len
                titleHtml: disableEngagement ? '<div style="height: 20px; width: {{width}}; color: #5e6473; font-style: italic;">engagement<span class="fa-regular fa-circle-info" style="color: #444444; margin-left: 10px; font-size: 1.125rem"></span></div>' : '',
                // eslint-disable-next-line max-len
                titleTooltip: disableEngagement ? this.localizationService.transform('Social.agentWidget.archiveSearch.results.charts.noEngagement.tooltip') : '',
                labelDisplayOptions: [
                    'percent'
                ],
                noContentLabel: this.noContentLabel,
                noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange')
            } as ChartMetadataModel;

            this.shareOfVoiceBySocialNetworkSubCharts.push(
                new MultiSeriesPieChartModel(series, options, metadata)
            );
        }

        this.shareOfVoiceBySocialNetworkChartOptions = JSON.parse(JSON.stringify(this.themeAnalyticsChartOptionsService.getChartOptions(
            ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK
        )));

        // if the account has access to tiktok, to include it in available networks for create/edit social topics
        if (this.enabledNetworkTypes.length && this.enabledNetworkTypes.includes(NetworkTypeEnum.tiktok)
            || this.socialSeriesService.allNetworkTypes.includes(NetworkTypeEnum.tiktok) && this.enabledNetworkTypes.includes(NetworkTypeEnum.all)
        ) {
            const tiktokIndex =
                this.shareOfVoiceBySocialNetworkChartOptions?.customLegendItems?.findIndex(mediaOptions => mediaOptions.name === 'TikTok');
            if (tiktokIndex === -1) {
                const tikTokItem: CustomLegendContainerItem & CustomLegendItem = {
                    name: this.localizationService.transform(`MediaType.${MediaTypeEnum.TIKTOK}`),
                    fill: this.defaultChartColors.socialNetworkColors.tiktok,
                    color: null,
                    label: null
                };
                this.shareOfVoiceBySocialNetworkChartOptions.customLegendItems.push(tikTokItem);
            }
        }

        this.shareOfVoiceBySocialNetworkChartMetadata = {
            type: null,
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialNetwork'),
            dateRangeLabelText: null,
            labelDisplayOptions: [
                LabelDisplayOptionsEnum.percent
            ],
            noContentLabel: this.noContentLabel,
            enableDataDisplaySettings: false
        } as ChartMetadataModel;

        this.shareOfVoiceBySocialNetworkLoading = true;
        if (this.shareOfVoiceBySocialNetworkSubscription) {
            this.shareOfVoiceBySocialNetworkSubscription.unsubscribe();
        }
        this.shareOfVoiceBySocialNetworkSubscription = combineLatest([
            this.previousPeriodShareOfVoiceBySocialNetworkVolumeSeries.dataProvider,
            this.shareOfVoiceBySocialNetworkVolumeSeries.dataProvider,
            this.previousPeriodShareOfVoiceBySocialNetworkImpressionsSeries.dataProvider,
            this.shareOfVoiceBySocialNetworkImpressionsSeries.dataProvider,
            this.previousPeriodShareOfVoiceBySocialNetworkEngagementsSeries.dataProvider,
            this.shareOfVoiceBySocialNetworkEngagementsSeries.dataProvider
        ]).subscribe(
            ([d1, d2, d3, d4, d5, d6]) => {
                this.shareOfVoiceBySocialNetworkLoading = true;
                if (d1 && d2 && d3 && d4 && d5 && d6) {
                    requestAnimationFrame(() => {
                        this.shareOfVoiceBySocialNetworkLoading = false;
                    });
                }
            }
        );

        this.shareOfVoiceBySocialNetwork = new ChartGroupInputModel(
            this.shareOfVoiceBySocialNetworkSubCharts,
            this.shareOfVoiceBySocialNetworkChartOptions,
            this.shareOfVoiceBySocialNetworkChartMetadata
        );
    }

    public initializeTopPosts(): void {
        this.topPostsSeries = new ReportSeriesModel();
        this.topPostsSeries.mentionProvider = new BehaviorSubject<Mention[]>(null);
        this.topPostsSortField = SearchCriteriaSortFieldEnum.audience_reach;
        this.topPostsSeries.mentions.mentionRequestGenerator = () => {
            return {
                sortOrder: SortOrderEnum.descending,
                sortField: SearchCriteriaSortFieldEnum.audience_reach,
                pageSize: 5
            };
        };
        this.topPostsSeries.mentions.mentionDataMapper = (response) => {
            return response.mentions.map(apiMention => new Mention(apiMention));
        };
        this.topPostsSeries.mentions.filters = new MentionSearchModel();
    }

    public initializeShareOfVoiceBySocialTopic() {
        const subChartTitles = [
            this.localizationService.transform('Social.intelligence.themeAnalytics.posts'),
            this.localizationService.transform('Social.intelligence.themeAnalytics.impressions'),
            this.localizationService.transform('Social.intelligence.themeAnalytics.engagements'),
        ];

        this.shareOfVoiceBySocialTopicSubCharts = [];

        // Set up options early, so the colors are sorted and accessible to the data mappers
        this.shareOfVoiceBySocialTopicChartOptions = JSON.parse(JSON.stringify(this.themeAnalyticsChartOptionsService.getChartOptions(
            ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK
        )));

        this.shareOfVoiceBySocialTopicChartOptions.customLegendItems = this.agentIds
            .sort((a, b) => a - b)
            .map(
                (id, index) => {
                    return {
                        name: this.socialTopics.find(socialTopic => socialTopic.id === +id).name,
                        fill: this.chartColors?.[index % this.chartColors?.length]
                            ?? defaultSocialColorMap['' + (index % Object.values(defaultSocialColorMap).length)],
                        id: id
                    };
                }
            );

        // volume series setup
        this.shareOfVoiceBySocialTopicVolumeSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialTopicVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: socialTopicNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialTopicVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel>{
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.count,
                        viewMentionUrl: this.shareOfVoiceBySocialTopicVolumeSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };

        // previous volume series setup
        this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: socialTopicNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel>{
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.count,
                        viewMentionUrl: this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };

        // impressions series setup
        this.shareOfVoiceBySocialTopicImpressionsSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialTopicImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: socialTopicNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialTopicImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel>{
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.socialTopicImpressions][0].count,
                        viewMentionUrl: this.shareOfVoiceBySocialTopicImpressionsSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };

        // previous impressions setup
        this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel>{
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.socialTopicImpressions][0].count,
                        viewMentionUrl: this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };
        this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries.mentions.isPreviousPeriod = true;

        // engagement series setup
        this.shareOfVoiceBySocialTopicEngagementsSeries = new ReportSeriesModel();
        this.shareOfVoiceBySocialTopicEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.shareOfVoiceBySocialTopicEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel> {
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.socialTopicEngagements][0].count,
                        viewMentionUrl: this.shareOfVoiceBySocialTopicEngagementsSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };

        // previous engagement series setup
        this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries.label = this.localizationService.transform(
            'Social.intelligence.themeAnalytics.previousPeriod'
        );
        this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets
                .filter(bucket => this.agentIds.find(id => id === +bucket.termValue))
                .sort((a, b) => +a.termValue - +b.termValue)
                .map((bucket, index) => {
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.agent.include = [ +bucket.termValue];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                    const color = (this.shareOfVoiceBySocialTopicChartOptions.customLegendItems as Array<any>).find(x => x.id === +bucket.termValue).fill;

                    return <DataPointModel> {
                        id: '' + index,
                        label: this.socialTopics.find(socialTopic => socialTopic.id === +bucket.termValue).name,
                        xValue: bucket.buckets[SocialSeriesNeoFacetKeysEnum.socialTopicEngagements][0].count,
                        viewMentionUrl: this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries.getSocialSeriesUrl(filterOverrides),
                        color: color
                    };
                });
        };
        this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries.mentions.isPreviousPeriod = true;

        // creating the chart model by joining series, options and metadata for all three double pie charts
        for (let i = 0; i < 3; i++) {
            const series = [
                [this.previousPeriodShareOfVoiceBySocialTopicVolumeSeries, this.shareOfVoiceBySocialTopicVolumeSeries],
                [this.previousPeriodShareOfVoiceBySocialTopicImpressionsSeries, this.shareOfVoiceBySocialTopicImpressionsSeries],
                [this.previousPeriodShareOfVoiceBySocialTopicEngagementsSeries, this.shareOfVoiceBySocialTopicEngagementsSeries]
            ][i];
            const options = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_TOPIC_SUB);
            const metadata = {
                titleText: subChartTitles[i],
                labelDisplayOptions: [
                    'percent'
                ],
                noContentLabel: this.noContentLabel,
                noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange')
            } as ChartMetadataModel;

            this.shareOfVoiceBySocialTopicSubCharts.push(
                new MultiSeriesPieChartModel(series, options, metadata)
            );
        }

        this.shareOfVoiceBySocialTopicChartMetadata = {
            type: ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_TOPIC,
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialTopic'),
            dateRangeLabelText: null,
            labelDisplayOptions: [
                LabelDisplayOptionsEnum.percent
            ],
            noContentLabel: this.noContentLabel,
            enableDataDisplaySettings: false
        } as ChartMetadataModel;

        this.shareOfVoiceBySocialTopic = new ChartGroupInputModel(
            this.shareOfVoiceBySocialTopicSubCharts,
            this.shareOfVoiceBySocialTopicChartOptions,
            this.shareOfVoiceBySocialTopicChartMetadata
        );
    }

    public initializeSentimentSummaryChart() {
        this.sentimentSummarySeries = new ReportSeriesModel();
        this.sentimentSummarySeries.mentions.facetGenerator = () => {
            return {
                facet: [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };
        this.sentimentSummarySeries.mentions.dataMapper = getSentimentMapper(this.sentimentSummarySeries, this.localizationService);

        this.previousPeriodSentimentSummarySeries = new ReportSeriesModel();
        this.previousPeriodSentimentSummarySeries.label = this.localizationService.transform('Social.intelligence.themeAnalytics.previousPeriod');
        this.previousPeriodSentimentSummarySeries.mentions.isPreviousPeriod = true;
        this.previousPeriodSentimentSummarySeries.mentions.facetGenerator = () => {
            return {
                facet: [MentionSearchFacetTypesEnum.sentiment],
                identifier: ReportSeriesFacetGeneratorEnum.facet
            };
        };

        this.previousPeriodSentimentSummarySeries.mentions.dataMapper = getSentimentMapper(
            this.previousPeriodSentimentSummarySeries,
            this.localizationService
        );

        const mockOptions = this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY);
        mockOptions.legendContainerElement = this.sentimentSummaryChartLegend.nativeElement;

        const mockMetadata = {
            titleText: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.sentimentSummary'),
            dateRange: {
                type: 'RELATIVE',
                relativeStartOffsetH: -24,
                relativeEndOffsetH: 0
            },
            labelDisplayOptions: [
                'percent'
            ],
            noContentLabel: this.noContentLabel,
            noDataFromDateRangeLabel: this.localizationService.transform('Social.intelligence.themeAnalytics.chart.noDataFromDateRange'),
            enableDataDisplaySettings: false
        } as ChartMetadataModel;

        this.sentimentSummaryChart = new MultiSeriesPieChartModel(
            [this.previousPeriodSentimentSummarySeries, this.sentimentSummarySeries],
            mockOptions,
            mockMetadata
        );
    }

    public initializeSummaryBySocialNetworkChart(): void {
        // Data over time series
        this.summaryBySocialNetworkVolumeOverTime = new ReportSeriesModel();
        this.summaryBySocialNetworkVolumeOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.posts');
        this.summaryBySocialNetworkVolumeOverTime.color = this.defaultChartColors.otherColors.volume;
        this.summaryBySocialNetworkVolumeOverTime.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkVolumeOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkVolumeOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);

                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: bucket.count,
                        viewMentionUrl: this.summaryBySocialNetworkVolumeOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        this.summaryBySocialNetworkImpressionsOverTime = new ReportSeriesModel();
        this.summaryBySocialNetworkImpressionsOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.impressions');
        this.summaryBySocialNetworkImpressionsOverTime.color = this.defaultChartColors.otherColors.impressions;
        this.summaryBySocialNetworkImpressionsOverTime.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkImpressionsOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkImpressionsOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);
                    const impressionBucket = bucket.buckets[SocialSeriesNeoFacetKeysEnum.impressionsOverTimeFacet][0];
                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: impressionBucket.count,
                        viewMentionUrl: this.summaryBySocialNetworkImpressionsOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        this.summaryBySocialNetworkEngagementOverTime = new ReportSeriesModel();
        this.summaryBySocialNetworkEngagementOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.engagement');
        this.summaryBySocialNetworkEngagementOverTime.color = this.defaultChartColors.otherColors.engagements;
        this.summaryBySocialNetworkEngagementOverTime.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkEngagementOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkEngagementOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);
                    const engagementBucket = bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementsOverTimeFacet][0];

                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: engagementBucket.count,
                        viewMentionUrl: this.summaryBySocialNetworkEngagementOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        const mockMetadata = {
            noContentLabel: this.noContentLabel,
        } as ChartMetadataModel;

        this.summaryBySocialNetworkChart = new MultiSeriesDateAndValueXYChart(
            [this.summaryBySocialNetworkVolumeOverTime, this.summaryBySocialNetworkImpressionsOverTime, this.summaryBySocialNetworkEngagementOverTime],
            this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_NETWORK),
            mockMetadata
        );

        // Card series
        const networkString = this.localizationService.transform(`Social.intelligence.network.${this.currentShareOfVoiceSocialType}`);
        // Volume Card
        this.summaryBySocialNetworkVolumeSeries = new ReportSeriesModel();
        this.summaryBySocialNetworkVolumeSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkVolumeLabel.byNetwork', {network: networkString});
        this.summaryBySocialNetworkVolumeSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        this.summaryBySocialNetworkVolumeSeries.metric = SocialSeriesMetricsEnum.NETWORK_VOLUME;

        this.previousPeriodSummaryBySocialNetworkVolumeSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialNetworkVolumeSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.previousPeriodSummaryBySocialNetworkVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialNetworkVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        this.previousPeriodSummaryBySocialNetworkVolumeSeries.mentions.isPreviousPeriod = true;

        // Impressions card
        this.summaryBySocialNetworkImpressionsSeries = new ReportSeriesModel();
        this.summaryBySocialNetworkImpressionsSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkImpressionsLabel.byNetwork', {network: networkString});
        this.summaryBySocialNetworkImpressionsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            );

            return [{
                count: count
            }];
        };
        this.summaryBySocialNetworkImpressionsSeries.metric = SocialSeriesMetricsEnum.NETWORK_IMPRESSIONS;

        this.previousPeriodSummaryBySocialNetworkImpressionsSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialNetworkImpressionsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodSummaryBySocialNetworkImpressionsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.previousPeriodSummaryBySocialNetworkImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialNetworkImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            );

            return [{
                count: count
            }];
        };

        // Engagement Card
        this.summaryBySocialNetworkEngagementsSeries = new ReportSeriesModel();
        this.summaryBySocialNetworkEngagementsSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkEngagementsLabel.byNetwork', {network: networkString});
        this.summaryBySocialNetworkEngagementsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.summaryBySocialNetworkEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialNetworkEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            );

            return [{
                count: count
            }];
        };
        this.summaryBySocialNetworkEngagementsSeries.metric = SocialSeriesMetricsEnum.NETWORK_ENGAGEMENTS;

        this.previousPeriodSummaryBySocialNetworkEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialNetworkEngagementsSeries.mentions.filters.searchCriteria.query.includedMediaTypes =
            networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        this.previousPeriodSummaryBySocialNetworkEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialNetworkEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            );

            return [{
                count: count
            }];
        };
        this.previousPeriodSummaryBySocialNetworkEngagementsSeries.mentions.isPreviousPeriod = true;
    }

    setSummaryBySocialNetworkSocialType(selectedType: NetworkTypeEnum) {
        this.currentShareOfVoiceSocialType = selectedType;
        const networkString = this.localizationService.transform(`Social.intelligence.network.${selectedType}`);

        const summaryBySocialNetworkSeries = [
            this.summaryBySocialNetworkVolumeOverTime,
            this.summaryBySocialNetworkImpressionsOverTime,
            this.summaryBySocialNetworkEngagementOverTime,
            // Cards
            this.summaryBySocialNetworkVolumeSeries,
            this.isArchiveSearch ? null : this.previousPeriodSummaryBySocialNetworkVolumeSeries,
            this.summaryBySocialNetworkImpressionsSeries,
            this.isArchiveSearch ? null : this.previousPeriodSummaryBySocialNetworkImpressionsSeries,
            this.summaryBySocialNetworkEngagementsSeries,
            this.isArchiveSearch ? null : this.previousPeriodSummaryBySocialNetworkEngagementsSeries
        ].filter(x => !!x);

        // Check to see if all the series actually exist
        if (summaryBySocialNetworkSeries.some(s => !s)) {
            return;
        }

        this.summaryBySocialNetworkVolumeSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkVolumeLabel.byNetwork', {network: networkString});
        this.summaryBySocialNetworkImpressionsSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkImpressionsLabel.byNetwork', {network: networkString});
        this.summaryBySocialNetworkEngagementsSeries.label =
            this.localizationService.transform('Social.intelligence.themeAnalytics.chart.networkEngagementsLabel.byNetwork', {network: networkString});

        this.socialSeriesService.deregisterSeries(summaryBySocialNetworkSeries);

        for (const singleSeries of summaryBySocialNetworkSeries) {
            singleSeries.mentions.filters = new MentionSearchModel();
            singleSeries.mentions.filters.searchCriteria.query.includedMediaTypes = networkTypeToMediaTypeModelMap.get(this.currentShareOfVoiceSocialType);
        }
        this.socialSeriesService.fulfillSeries(summaryBySocialNetworkSeries);
    }

    public initializeSummaryBySocialTopicChart(): void {
        // Data over time series
        this.summaryBySocialTopicVolumeOverTime = new ReportSeriesModel();
        this.summaryBySocialTopicVolumeOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.volume');
        this.summaryBySocialTopicVolumeOverTime.color = this.defaultChartColors.otherColors.volume;
        this.summaryBySocialTopicVolumeOverTime.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicVolumeOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicVolumeOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }

            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);

                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    filterOverrides.query.agent.include = this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: bucket.count,
                        viewMentionUrl: this.summaryBySocialTopicVolumeOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        this.summaryBySocialTopicImpressionsOverTime = new ReportSeriesModel();
        this.summaryBySocialTopicImpressionsOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.impressions');
        this.summaryBySocialTopicImpressionsOverTime.color = this.defaultChartColors.otherColors.impressions;
        this.summaryBySocialTopicImpressionsOverTime.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicImpressionsOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicImpressionsOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);
                    const impressionBucket = bucket.buckets[SocialSeriesNeoFacetKeysEnum.impressionsOverTimeFacet][0];

                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    filterOverrides.query.agent.include = this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.audience_reach;

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: impressionBucket.count,
                        viewMentionUrl: this.summaryBySocialTopicImpressionsOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        this.summaryBySocialTopicEngagementOverTime = new ReportSeriesModel();
        this.summaryBySocialTopicEngagementOverTime.label = this.localizationService.transform('Social.intelligence.themeAnalytics.engagement');
        this.summaryBySocialTopicEngagementOverTime.color = this.defaultChartColors.otherColors.engagements;
        this.summaryBySocialTopicEngagementOverTime.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicEngagementOverTime.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequestOverTime,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicEngagementOverTime.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.overTimeFacet);
            if (!neoFacet) {
                return [];
            }
            return neoFacet?.buckets.map(
                bucket => {
                    const timeScale = getTimeScale(bucket.intervalValue.start, bucket.intervalValue.end);
                    const engagementBucket = bucket.buckets[SocialSeriesNeoFacetKeysEnum.engagementsOverTimeFacet][0];

                    const filterOverrides = new MentionSearchCriteriaModel();
                    filterOverrides.query.dateQuery = DateQueryModel.getAbsoluteQuery();
                    filterOverrides.query.dateQuery.absoluteRange = {
                        start: new Date(bucket.intervalValue.start),
                        end: new Date(bucket.intervalValue.end)
                    };
                    filterOverrides.query.agent.include = this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
                    filterOverrides.sortField = SearchCriteriaSortFieldEnum.engagement_total;

                    return <DataPointModel>{
                        rawDateFacetFrom: bucket.intervalValue.start,
                        rawDateFacetTo: bucket.intervalValue.end,
                        startTime: moment(bucket.intervalValue.start).format(this.dateBulletFormat),
                        endTime: moment(bucket.intervalValue.end).format(this.dateBulletFormat),
                        xValueStartTime: moment(bucket.intervalValue.start).startOf(timeScale).format(this.dateBulletFormat),
                        xValueEndTime: moment(bucket.intervalValue.end).startOf(timeScale).format(this.dateBulletFormat),
                        xValue: bucket.intervalValue.start,
                        yValue: engagementBucket.count,
                        viewMentionUrl: this.summaryBySocialTopicEngagementOverTime.getSocialSeriesUrl(filterOverrides)
                    };
                }
            );
        };

        const mockMetadata = {
            type: ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_TOPIC,
            noContentLabel: this.noContentLabel,
        } as ChartMetadataModel;

        this.summaryBySocialTopicChart = new MultiSeriesDateAndValueXYChart(
            [this.summaryBySocialTopicVolumeOverTime, this.summaryBySocialTopicImpressionsOverTime, this.summaryBySocialTopicEngagementOverTime],
            this.themeAnalyticsChartOptionsService.getChartOptions(ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_TOPIC),
            mockMetadata
        );

        // Card series
        // Volume Card
        this.summaryBySocialTopicVolumeSeries = new ReportSeriesModel();
        this.summaryBySocialTopicVolumeSeries.label = 'Social.intelligence.themeAnalytics.posts';
        this.summaryBySocialTopicVolumeSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        this.summaryBySocialTopicVolumeSeries.metric = SocialSeriesMetricsEnum.VOLUME;

        this.previousPeriodSummaryBySocialTopicVolumeSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialTopicVolumeSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.previousPeriodSummaryBySocialTopicVolumeSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialTopicVolumeSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce((previousValue, bucket) => previousValue + bucket.count, 0) || 0;
            return [{
                count: count
            }];
        };
        this.previousPeriodSummaryBySocialTopicVolumeSeries.mentions.isPreviousPeriod = true;

        // Impressions card
        this.summaryBySocialTopicImpressionsSeries = new ReportSeriesModel();
        this.summaryBySocialTopicImpressionsSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicImpressionsSeries.label = 'Social.intelligence.themeAnalytics.impressions';
        this.summaryBySocialTopicImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            ) || 0;

            return [{
                count: count
            }];
        };
        this.summaryBySocialTopicImpressionsSeries.metric = SocialSeriesMetricsEnum.IMPRESSIONS;

        this.previousPeriodSummaryBySocialTopicImpressionsSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialTopicImpressionsSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.previousPeriodSummaryBySocialTopicImpressionsSeries.mentions.isPreviousPeriod = true;
        this.previousPeriodSummaryBySocialTopicImpressionsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialTopicImpressionsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) =>
                    previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions][0].count
                , 0
            ) || 0;

            return [{
                count: count
            }];
        };

        // Engagement Card
        this.summaryBySocialTopicEngagementsSeries = new ReportSeriesModel();
        this.summaryBySocialTopicEngagementsSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.summaryBySocialTopicEngagementsSeries.label = 'Social.intelligence.themeAnalytics.engagements';
        this.summaryBySocialTopicEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.summaryBySocialTopicEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            ) || 0;

            return [{
                count: count
            }];
        };
        this.summaryBySocialTopicEngagementsSeries.metric = SocialSeriesMetricsEnum.ENGAGEMENTS;

        this.previousPeriodSummaryBySocialTopicEngagementsSeries = new ReportSeriesModel();
        this.previousPeriodSummaryBySocialTopicEngagementsSeries.mentions.filters.searchCriteria.query.agent.include =
            this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : [];
        this.previousPeriodSummaryBySocialTopicEngagementsSeries.mentions.facetGenerator = () => {
            return {
                facet: mediaTypeNeoFacetRequest,
                identifier: ReportSeriesFacetGeneratorEnum.neoFacet
            };
        };
        this.previousPeriodSummaryBySocialTopicEngagementsSeries.mentions.dataMapper = (response) => {
            const neoFacet = response.neoFacetResults.find(facet => facet.key === SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup);
            const count = neoFacet?.buckets.reduce(
                (previousValue, bucket) => previousValue + bucket.buckets[SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements][0].count,
                0
            ) || 0;

            return [{
                count: count
            }];
        };
        this.previousPeriodSummaryBySocialTopicEngagementsSeries.mentions.isPreviousPeriod = true;
    }

    public onSummaryBySocialTopicSelectionChanged() {
        const summaryBySocialTopicSeries = [
            this.summaryBySocialTopicVolumeOverTime,
            this.summaryBySocialTopicImpressionsOverTime,
            this.summaryBySocialTopicEngagementOverTime,
            this.summaryBySocialTopicVolumeSeries,
            this.previousPeriodSummaryBySocialTopicVolumeSeries,
            this.summaryBySocialTopicImpressionsSeries,
            this.previousPeriodSummaryBySocialTopicImpressionsSeries,
            this.summaryBySocialTopicEngagementsSeries,
            this.previousPeriodSummaryBySocialTopicEngagementsSeries
        ];

        this.socialSeriesService.deregisterSeries(summaryBySocialTopicSeries);

        summaryBySocialTopicSeries.forEach(
            singleSeries => singleSeries.mentions.filters.searchCriteria.query.agent.include =
                this.summaryBySocialTopicSelection ? [this.summaryBySocialTopicSelection.value] : []
        );

        this.socialSeriesService.fulfillSeries(summaryBySocialTopicSeries);
    }

    public exportChart(event: ChartExportEventModel, elementRef: any, chartType?: ThemeAnalyticsChartTypeEnum) {
        const exportType = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        const exportModel = this.prepareExportData(chartType);
        if (exportType === 'csv' || exportType === 'json') {
            this.chartExportService.exportData(exportType, exportModel);
        } else {
            const exportMenuElement = elementRef.querySelector('.agpr-themeAnalyticsSummary-exportMenu');
            exportMenuElement.style.display = 'none';
            this.chartExportService.exportImage(exportType, elementRef, exportModel.title).then(() => {
                exportMenuElement.style.display = 'block';
            });
        }
    }
    private prepareExportData(chartType: ThemeAnalyticsChartTypeEnum): SocialChartExportModel {
        switch (chartType) {
            case ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_NETWORK_SUB:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialNetwork'),
                    exportData: this.chartExportService.setExportDataForChartGroup(this.shareOfVoiceBySocialNetwork.charts),
                    exportLabelDefinitions: new Map<string, string>([
                        ['label', this.localizationService.transform( 'Social.export.label.mediaType')],
                        ['xValue_VOLUME0', this.localizationService.transform( 'Social.export.label.previousVolume')],
                        ['xValue_VOLUME1', this.localizationService.transform( 'Social.export.label.volume')],
                        ['xValue_IMPRESSIONS0', this.localizationService.transform( 'Social.export.label.previousReach')],
                        ['xValue_IMPRESSIONS1', this.localizationService.transform( 'Social.export.label.reach')],
                        ['xValue_ENGAGEMENTS0', this.localizationService.transform( 'Social.export.label.previousEngagements')],
                        ['xValue_ENGAGEMENTS1', this.localizationService.transform( 'Social.export.label.engagements')],
                    ])
                };
            case ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_NETWORK:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summaryBySocialNetwork'),
                };
            case ThemeAnalyticsChartTypeEnum.SHARE_OF_VOICE_BY_SOCIAL_TOPIC_SUB:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.shareOfVoiceBySocialTopic'),
                    exportData: this.chartExportService.setExportDataForChartGroup(this.shareOfVoiceBySocialTopic.charts),
                    exportLabelDefinitions: new Map<string, string>([
                        ['label', this.localizationService.transform( 'Social.export.label.socialTopic')],
                        ['xValue_POSTS0', this.localizationService.transform( 'Social.export.label.previousPosts')],
                        ['xValue_POSTS1', this.localizationService.transform( 'Social.export.label.posts')],
                        ['xValue_IMPRESSIONS0', this.localizationService.transform( 'Social.export.label.previousReach')],
                        ['xValue_IMPRESSIONS1', this.localizationService.transform( 'Social.export.label.reach')],
                        ['xValue_ENGAGEMENTS0', this.localizationService.transform( 'Social.export.label.previousEngagements')],
                        ['xValue_ENGAGEMENTS1', this.localizationService.transform( 'Social.export.label.engagements')],
                    ])
                };
            case ThemeAnalyticsChartTypeEnum.SUMMARY_BY_SOCIAL_TOPIC:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summaryBySocialTopic'),
                };
            case ThemeAnalyticsChartTypeEnum.SENTIMENT_SUMMARY:
                return {
                    title: this.localizationService.transform('Social.intelligence.themeAnalytics.summary.sentimentSummary'),
                    chartModel: this.sentimentSummaryChart
                };
            default:
                break;
        }
        return;
    }
}

// @TODO: Move these to their own file for re-use
const mediaTypeNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MEDIA_TYPE,
    key: SocialSeriesNeoFacetKeysEnum.mediaTypeFacetGroup,
    engagementBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.mediaTypeEngagements,
            field: NeoFacetTermFieldApiEnum.ENGAGEMENT_TOTAL,
            engagementAge: EngagementAgeApiEnum.LATEST,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
    ],
    aggregateBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.mediaTypeImpressions,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};

const socialTopicNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.MATCHED_AGENTS,
    key: SocialSeriesNeoFacetKeysEnum.socialTopicFacetGroup,
    engagementBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.socialTopicEngagements,
            field: NeoFacetTermFieldApiEnum.ENGAGEMENT_TOTAL,
            engagementAge: EngagementAgeApiEnum.LATEST,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
    ],
    aggregateBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.socialTopicImpressions,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};

const sentimentNeoFacetRequest: NeoFacetRequestApiModel = {
    termField: NeoFacetTermFieldApiEnum.SENTIMENT,
    key: SocialSeriesNeoFacetKeysEnum.sentimentFacetGroup,
    plainBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.sentimentByMediaTypeFacetGroup,
            field: NeoFacetTermFieldApiEnum.MEDIA_TYPE
        }
    ]
};

const mediaTypeNeoFacetRequestOverTime: NeoFacetRequestApiModel = {
    dateField: NeoFacetDateFieldApiEnum.CREATED_AT,
    key: SocialSeriesNeoFacetKeysEnum.overTimeFacet,
    engagementBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.engagementsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.ENGAGEMENT_TOTAL,
            engagementAge: EngagementAgeApiEnum.LATEST,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        },
    ],
    aggregateBucketRequests: [
        {
            key: SocialSeriesNeoFacetKeysEnum.impressionsOverTimeFacet,
            field: NeoFacetTermFieldApiEnum.AUDIENCE_REACH,
            function: BucketFunctionApiEnum.INDIVIDUAL_SUM
        }
    ]
};

export const getSentimentMapper = (series: ReportSeriesModel, localizationService: LocalizationService) => {
    return (response) => {
        let positiveViewMentionUrl: string;
        let neutralViewMentionUrl: string;
        let notAvailableViewMentionUrl: string;
        let negativeViewMentionUrl: string;

        // If we can't make one, it's fine, it won't be returned anyway
        try {
            const positiveFilterOverrides = new MentionSearchCriteriaModel();
            positiveFilterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.positive)];
            positiveViewMentionUrl = series.getSocialSeriesUrl(positiveFilterOverrides);
        } catch {}
        try {
            const neutralFilterOverrides = new MentionSearchCriteriaModel();
            neutralFilterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.neutral)];
            neutralViewMentionUrl = series.getSocialSeriesUrl(neutralFilterOverrides);
        } catch {}
        try {
            const notAvailableFilterOverrides = new MentionSearchCriteriaModel();
            notAvailableFilterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.notAvailable)];
            notAvailableViewMentionUrl = series.getSocialSeriesUrl(notAvailableFilterOverrides);
        } catch {}
        try {
            const negativeFilterOverrides = new MentionSearchCriteriaModel();
            negativeFilterOverrides.query.sentiments = [new SentimentModel(SearchCriteriaQuerySentimentsEnum.negative)];
            negativeViewMentionUrl = series.getSocialSeriesUrl(negativeFilterOverrides);
        } catch {}

        const returnArray: DataPointModel[] = [
            {
                id: SearchCriteriaQuerySentimentsEnum.positive,
                label: localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.positive}`),
                xValue: 0,
                viewMentionUrl: positiveViewMentionUrl
            },
            {
                id: SearchCriteriaQuerySentimentsEnum.neutral,
                label: localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.neutral}`),
                xValue: 0,
                viewMentionUrl: neutralViewMentionUrl
            },
            {
                id: SearchCriteriaQuerySentimentsEnum.notAvailable,
                label: localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.notAvailable}`),
                xValue: 0,
                viewMentionUrl: notAvailableViewMentionUrl
            },
            {
                id: SearchCriteriaQuerySentimentsEnum.negative,
                label: localizationService.transform(`Sentiment.${SearchCriteriaQuerySentimentsEnum.negative}`),
                xValue: 0,
                viewMentionUrl: negativeViewMentionUrl
            },
        ];

        if (response && response.facetResults) {
            for (const facetType of response.facetResults) {
                if (facetType.field !== MentionSearchFacetTypesEnum.sentiment) {
                    continue;
                }

                for (const facetValue of facetType.values) {
                    const floatValue = parseFloat(facetValue.value);

                    if (isNaN(floatValue)) {
                        returnArray[2].xValue += facetValue.count;

                        continue;
                    }

                    // Negative -1 to -0.3
                    // Positive 0.3 to 1
                    // Neutral -0.2 to 0.2
                    if (floatValue <= -0.3) {
                        returnArray[3].xValue += facetValue.count;
                    } else if (floatValue >= 0.3) {
                        returnArray[0].xValue += facetValue.count;
                    } else {
                        returnArray[1].xValue += facetValue.count;
                    }
                }
            }
        }

        return returnArray;
    };
};
